import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Select, SelectItem } from '@heroui/react';
import { PasswordInput } from '@/components/common/PasswordInput';
import { useMailboxesList } from '@/features/mailboxes/hooks';
import { useCreateMigration, useVerifySource } from '@/features/migrations/hooks';
import { createMigrationSchema, type CreateMigrationValues } from '@/features/migrations/schema';
import { PROVIDER_PRESETS, defaultPort, presetByKey } from '@/features/migrations/presets';
import type { VerifySourceResult } from '@/features/migrations/types';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@/icons';

/**
 * Start an IMAP migration: pick the destination mailbox and the source provider, test the
 * connection (optional, nothing stored), then queue the job. The password is sent once and
 * kept encrypted only until the job ends.
 */
export function MigrationFormModal({ isOpen, onClose, mailboxId, onCreated }: {
  isOpen: boolean; onClose: () => void; mailboxId?: string; onCreated?: (id: string) => void;
}) {
  const create = useCreateMigration();
  const verify = useVerifySource();
  const mailboxes = useMailboxesList({ page: 1, pageSize: 200 });
  const [presetKey, setPresetKey] = useState('other');
  const [check, setCheck] = useState<VerifySourceResult | null>(null);
  const preset = presetByKey(presetKey);

  const form = useForm<CreateMigrationValues>({
    resolver: zodResolver(createMigrationSchema),
    defaultValues: { destinationMailboxId: mailboxId ?? '', sourceHost: '', sourcePort: 993, sourceSecurity: 'ssl', sourceUsername: '', sourcePassword: '' },
  });
  const { register, formState: { errors } } = form;

  useEffect(() => {
    if (!isOpen) return;
    form.reset({ destinationMailboxId: mailboxId ?? '', sourceHost: '', sourcePort: 993, sourceSecurity: 'ssl', sourceUsername: '', sourcePassword: '' });
    setPresetKey('other');
    setCheck(null);
  }, [isOpen, mailboxId]); // eslint-disable-line

  // Any change to the source settings invalidates an earlier connection test.
  useEffect(() => {
    const sub = form.watch((_v, { name }) => { if (name && name !== 'destinationMailboxId') setCheck(null); });
    return () => sub.unsubscribe();
  }, [form]);

  const applyPreset = (key: string) => {
    const p = presetByKey(key);
    setPresetKey(key);
    form.setValue('sourceHost', p.host);
    form.setValue('sourcePort', p.port);
    form.setValue('sourceSecurity', p.security === 'none' ? 'ssl' : p.security);
  };

  const testConnection = async () => {
    const ok = await form.trigger(['sourceHost', 'sourcePort', 'sourceSecurity', 'sourceUsername', 'sourcePassword']);
    if (!ok) return;
    const v = form.getValues();
    try {
      setCheck(await verify.mutateAsync({ sourceHost: v.sourceHost, sourcePort: Number(v.sourcePort), sourceSecurity: v.sourceSecurity, sourceUsername: v.sourceUsername, sourcePassword: v.sourcePassword }));
    } catch (e) {
      setCheck({ ok: false, message: (e as Error).message || 'The connection test failed.' });
    }
  };

  const submit = form.handleSubmit(async (v) => {
    const job = await create.mutateAsync({ ...v, sourcePort: Number(v.sourcePort) });
    onClose();
    onCreated?.(job.id);
  });

  const mailboxItems = (mailboxes.data?.data ?? []).filter((m) => m.status === 'active');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="text-fg">Migrate mail from another provider</ModalHeader>
        <ModalBody>
          <form id="migration-form" onSubmit={submit} className="space-y-4" noValidate>
            <Select label="Import into Nifty mailbox" variant="bordered" isLoading={mailboxes.isLoading}
              items={mailboxItems}
              selectedKeys={form.watch('destinationMailboxId') ? new Set([form.watch('destinationMailboxId')]) : new Set()}
              onSelectionChange={(k) => form.setValue('destinationMailboxId', String(Array.from(k)[0] ?? ''), { shouldValidate: true })}
              isInvalid={!!errors.destinationMailboxId} errorMessage={errors.destinationMailboxId?.message}
              description="Existing mail in the mailbox is kept; duplicates are skipped.">
              {(m) => <SelectItem key={m.id} textValue={m.address}>{m.address}</SelectItem>}
            </Select>

            <Select label="Current provider" variant="bordered" selectedKeys={new Set([presetKey])}
              onSelectionChange={(k) => applyPreset(String(Array.from(k)[0] ?? 'other'))}>
              {PROVIDER_PRESETS.map((p) => <SelectItem key={p.key}>{p.label}</SelectItem>)}
            </Select>
            {preset.note && (
              <p className={`flex gap-2 rounded-lg p-3 text-sm ${preset.unsupported ? 'bg-warning/10 text-warning' : 'bg-info/10 text-fg-muted'}`}>
                {preset.unsupported ? <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" /> : <InformationCircleIcon className="h-5 w-5 shrink-0 text-info" aria-hidden="true" />}
                <span>{preset.note}</span>
              </p>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_7rem_10rem]">
              <Input {...register('sourceHost')} label="IMAP server" placeholder="imap.example.com" variant="bordered"
                isInvalid={!!errors.sourceHost} errorMessage={errors.sourceHost?.message} autoComplete="off" />
              <Input {...register('sourcePort')} type="number" label="Port" variant="bordered" min={1} max={65535}
                isInvalid={!!errors.sourcePort} errorMessage={errors.sourcePort?.message} />
              <Select label="Security" variant="bordered" selectedKeys={new Set([form.watch('sourceSecurity')])}
                onSelectionChange={(k) => {
                  const s = String(Array.from(k)[0] ?? 'ssl') as 'ssl' | 'starttls';
                  form.setValue('sourceSecurity', s);
                  form.setValue('sourcePort', defaultPort(s));
                }}>
                <SelectItem key="ssl">SSL/TLS</SelectItem>
                <SelectItem key="starttls">STARTTLS</SelectItem>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input {...register('sourceUsername')} label="Username" placeholder="usually the full email address" variant="bordered"
                isInvalid={!!errors.sourceUsername} errorMessage={errors.sourceUsername?.message} autoComplete="off" />
              <PasswordInput {...register('sourcePassword')} label="Password or app password" autoComplete="new-password"
                isInvalid={!!errors.sourcePassword} errorMessage={errors.sourcePassword?.message} />
            </div>
            <p className="text-xs text-fg-subtle">
              The password is encrypted and used only by this migration; it is deleted as soon as the migration ends. The old mailbox is read only — nothing there is changed or deleted.
            </p>

            {check && (
              <p role="status" className={`flex gap-2 rounded-lg p-3 text-sm ${check.ok ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                {check.ok ? <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" /> : <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />}
                <span>{check.message}</span>
              </p>
            )}
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button variant="bordered" onPress={testConnection} isLoading={verify.isPending} isDisabled={preset.unsupported}>Test connection</Button>
          <Button color="primary" type="submit" form="migration-form" isLoading={form.formState.isSubmitting} isDisabled={preset.unsupported}>
            Start migration
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
